import { useCallback } from 'react'

import * as Sentry from '@sentry/react'
import dayjs from 'dayjs'

import { base64Encode } from 'src/libs/helper'
import { RoomCsvTranslator, RoomTranslator } from 'src/translators'
import {
  ApplicationStatus,
  BulkUpdateRoomsFormEntity,
  RegistrationStatus,
  ImportCsvRoomResultEntity,
  RoomEntity,
  WriteRoomFormEntity,
} from 'src/types'
import {
  useBulkUpdateRoomsMutation,
  useUpdateRoomMutation,
  useUpdateRoomUserConfirmationCodeMutation,
  useCreateRoomsCsvMutation,
} from 'src/types/generated'

type CreateRoomsFromCsvInput = {
  projectId: string
  csvContent: string
  csvHeaderMapping: {
    roomNameColumn: string
    wingNameColumn: string
    floorColumn: string
    roomTypeNameColumn: string
    selectDisabledColumn: string
  }
  autoGenerateWing: boolean
  autoGenerateFloor: boolean
}

type BulkUpdateRoomsInput = {
  projectId: string
  formEntity: BulkUpdateRoomsFormEntity
  rooms: RoomEntity[]
}
type UpdateAdminMemoInput = {
  projectId: string
  room: RoomEntity
  adminMemo: string | undefined
}

type UpdateIsSelectEnabledInput = {
  projectId: string
  roomId: string
  isSelectEnabled: boolean
}

type UpdateConfirmationCodeInput = {
  projectId: string
  roomId: string
  confirmationCode: string
}

export const useRoom = () => {
  const [createRoomsCsvMutation] = useCreateRoomsCsvMutation()
  const [bulkUpdateRoomsMutation] = useBulkUpdateRoomsMutation()
  const [updateRoomMutation] = useUpdateRoomMutation()
  const [updateRoomUserConfirmationCodeMutation] = useUpdateRoomUserConfirmationCodeMutation()

  const createRoomsFromCsv = async (
    input: CreateRoomsFromCsvInput
  ): Promise<ImportCsvRoomResultEntity[]> => {
    const { projectId, csvContent, ...otherInput } = input

    const csvData = base64Encode(csvContent)
    const { data } = await createRoomsCsvMutation({
      variables: { projectId, params: { csvData, ...otherInput } },
    })

    const result = data?.createRoomsCSV
    if (!result) {
      Sentry.captureMessage('住戸csvを読み込んだ際にapiから期待した結果が返ってきていません')
      throw new Error('予期せぬエラーが発生しました')
    }

    return RoomCsvTranslator.toEntities(result ?? [])
  }

  const hasChanged = (formEntity: WriteRoomFormEntity, room: RoomEntity): boolean => {
    // NOTE:
    //  room.memo と formEntity.memo 値が入っていない場合、どちらも空文字・undefinedになるケースがある。
    //  これらの差異を吸収するため、空文字の場合はどちらもundefinedに変換する
    const formMemo = formEntity.memo === '' ? undefined : formEntity.memo
    const roomMemo = room.memo === '' ? undefined : room.memo

    const hasChangedWithoutSelectMasters =
      formEntity.name !== room.name ||
      formEntity.floor !== room.floor ||
      formEntity.wingId !== Number.parseInt(room.wing.id) ||
      formEntity.roomTypeId !==
        (room.roomType?.id ? Number.parseInt(room.roomType.id) : undefined) ||
      formEntity.isSelectEnabled !== room.isSelectEnabled ||
      formMemo !== roomMemo ||
      formEntity.registrationDueDate?.getTime() !== room.select.registerLimitAt?.getTime() ||
      // 既に存在する住戸で削除のみを実行した場合
      (!!formEntity.entityId && !!formEntity.isDeleted)

    // セレクト設問以外に変更があった場合は即座に変更ありと判定
    if (hasChangedWithoutSelectMasters) {
      return true
    }

    // 保存済みとして選択されていたセレクト設問のid（ただし回答のidは含んでいない）
    const selectedSelectMasterIdsInDatabase = new Set(room.select.selectedItemIds.map(Number))
    // 変更予定として選択されているセレクト設問のid（ただし回答のidは含んでいない）
    const selectedSelectMasterIdsToBeSaved = new Set(
      formEntity.selectMasters
        .filter(selectMaster => !selectMaster.isDeleted)
        .map(selectMaster => selectMaster.id)
    )

    // 各設問に対して変更がなかったかを判定
    return formEntity.selectMasters.some(selectMaster => {
      // 前回保存した際に選択せず、変更予定としても選択していない場合は変更がなかったと判断する
      if (
        !selectedSelectMasterIdsInDatabase.has(selectMaster.id) &&
        !selectedSelectMasterIdsToBeSaved.has(selectMaster.id)
      ) {
        return false
      }

      // 前回保存した際に選択していて、変更予定としても選択している、かつ、今回の変更で設問の操作をしなかった場合（セレクト設問のチェックOFF → ONの操作をしないとselectOptionMasterIdsに値が入らない）は変更がなかったことにする
      // NOTE:
      //  セレクト設問のチェックOFF → ONと操作した場合、selectOptionMasterIdsに値が必ず入る。
      //  しかし、前回保存した際の回答の情報は住戸の一覧の中に持っておらず、変更予定の回答部分との比較が出来ない。
      //  そのためselectOptionMasterIdsに値が入っている場合は必ず変更があったと判断する
      if (
        selectedSelectMasterIdsInDatabase.has(selectMaster.id) &&
        // 変更予定の選択に関しては isDeleted を考慮する必要があるため注意
        selectedSelectMasterIdsToBeSaved.has(selectMaster.id) &&
        selectMaster.selectOptionMasterIds.length === 0
      ) {
        return false
      }

      return true
    })
  }

  const extractModifiedRooms = (
    formEntity: BulkUpdateRoomsFormEntity,
    rooms: RoomEntity[]
  ): BulkUpdateRoomsFormEntity => {
    const modifiedRooms = formEntity.items.filter(item => {
      const targetRoom = rooms.find(room => room.id === item.entityId)
      if (!targetRoom) {
        return true
      }
      return hasChanged(item, targetRoom)
    })
    return { items: modifiedRooms }
  }

  const bulkUpdateRooms = async (input: BulkUpdateRoomsInput) => {
    const { projectId, formEntity, rooms } = input

    // 変更があるもののみを抽出
    const changedFormEntity = extractModifiedRooms(formEntity, rooms)

    const params = RoomTranslator.toRequest({ formEntity: changedFormEntity, beforeRooms: rooms })
    await bulkUpdateRoomsMutation({ variables: { projectId, params } })
  }

  const updateAdminMemo = async ({ projectId, room, adminMemo }: UpdateAdminMemoInput) => {
    await updateRoomMutation({
      variables: {
        projectId,
        roomId: Number(room.id),
        input: { adminMemo: adminMemo ?? '' },
      },
    })
  }

  const updateIsSelectEnabled = async (input: UpdateIsSelectEnabledInput) => {
    const { projectId, roomId, isSelectEnabled } = input
    await updateRoomMutation({
      variables: {
        projectId,
        roomId: Number(roomId),
        input: { isSelectEnabled },
      },
    })
  }

  const loadApplicationStatusText = useCallback((status: ApplicationStatus): string => {
    switch (status) {
      case 'waiting': {
        return '未申請'
      }
      case 'reviewing': {
        return '申請中'
      }
      case 'approved': {
        return '承認済'
      }
      case 'rejected': {
        return '非承認'
      }
      case 'canceled': {
        return '取下げ'
      }
      case 'approvalCanceled': {
        return '承認取下げ'
      }
      default: {
        return ''
      }
    }
  }, [])

  const loadApplicationStatus = (string: string): ApplicationStatus | undefined => {
    switch (string) {
      case '未申請': {
        return 'waiting'
      }
      case '申請中': {
        return 'reviewing'
      }
      case '承認済': {
        return 'approved'
      }
      case '非承認': {
        return 'rejected'
      }
      case '取下げ': {
        return 'canceled'
      }
      case '承認取下げ': {
        return 'approvalCanceled'
      }
      default: {
        return undefined
      }
    }
  }

  const loadRegistrationStatusText = useCallback((status: RegistrationStatus): string => {
    switch (status) {
      case 'waiting': {
        return ''
      }
      case 'registered': {
        return '登録済'
      }
      case 'agreed': {
        return '同意済'
      }
      default: {
        return ''
      }
    }
  }, [])

  const loadRegistrationStatus = (string: string): RegistrationStatus | undefined => {
    switch (string) {
      case '登録済': {
        return 'registered'
      }
      case '同意済': {
        return 'agreed'
      }
      case '': {
        return 'waiting'
      }
      default: {
        return undefined
      }
    }
  }

  const updateConfirmationCode = async (input: UpdateConfirmationCodeInput) => {
    const { projectId, roomId, confirmationCode } = input

    await updateRoomUserConfirmationCodeMutation({
      variables: {
        projectId,
        roomId: Number(roomId),
        params: { confirmationCode },
      },
    })
  }

  const getIsContractAgreementDeadlineNear = (registerLimitAt: Date | undefined): boolean => {
    const oneWeekAgo = dayjs().add(1, 'week')
    const today = dayjs()

    if (!registerLimitAt) {
      return false
    }

    const target = dayjs(registerLimitAt)
    return target.isAfter(today) && target.isBefore(oneWeekAgo)
  }

  const getIsContractAgreementDeadlineExpired = (registerLimitAt: Date | undefined): boolean => {
    return dayjs().isAfter(dayjs(registerLimitAt))
  }

  return {
    createRoomsFromCsv,
    extractModifiedRooms,
    bulkUpdateRooms,
    updateAdminMemo,
    updateIsSelectEnabled,
    loadApplicationStatusText,
    loadApplicationStatus,
    loadRegistrationStatusText,
    loadRegistrationStatus,
    updateConfirmationCode,
    getIsContractAgreementDeadlineNear,
    getIsContractAgreementDeadlineExpired,
  }
}
